import { useQuery } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'
import { ClockIcon, ListTodoIcon } from 'lucide-react'

import { portalApi } from '@/api/portal'
import { useCurrentUser } from '@/auth/use-auth'
import { PortalBookingShortcut } from '@/components/booking/portal-bookings'
import { Frame } from '@/components/frame'
import { PageHeader } from '@/components/page-header'
import { PhoneList } from '@/components/phone-display'
import { PortalTaskList } from '@/components/portal/portal-task-list'
import { PortalTodoBoard } from '@/components/portal/portal-todo-board'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { msg } from '@/i18n'
import { describeActivity } from '@/lib/activity'
import { formatRelative } from '@/lib/format'
import { isClosedStatus } from '@/lib/labels'
import { portalName } from '@/lib/portal'

export const Route = createFileRoute('/portal/')({
  component: PortalHome,
})

function PortalHome() {
  const user = useCurrentUser()
  const firstName = user.name.split(' ')[0] ?? user.name

  const tasks = useQuery({
    queryKey: ['portal', 'tasks', 'accueil'],
    queryFn: () => portalApi.tasks({ page_size: 50 }),
  })

  const activity = useQuery({
    queryKey: ['portal', 'activity'],
    queryFn: () => portalApi.activity({ page_size: 10 }),
  })

  const me = useQuery({ queryKey: ['portal', 'me'], queryFn: () => portalApi.me() })

  const items = tasks.data?.items ?? []
  const open = items.filter((task) => !isClosedStatus(task.status))
  const done = items.filter((task) => isClosedStatus(task.status))

  const t = msg().portalHome
  return (
    <div className="space-y-6">
      <PageHeader title={t.title(firstName)} documentTitle={t.documentTitle} />

      <PortalTodoBoard />

      <PortalBookingShortcut />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Card>
          <CardContent className="space-y-1 pt-5">
            <p className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
              <ListTodoIcon className="size-3.5" />
              {t.openRequests}
            </p>
            {tasks.isPending ? (
              <Skeleton className="h-8 w-12" />
            ) : (
              <p className="text-2xl font-semibold tabular-nums">{open.length}</p>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardContent className="space-y-1 pt-5">
            <p className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
              <ClockIcon className="size-3.5" />
              {t.doneRequests}
            </p>
            {tasks.isPending ? (
              <Skeleton className="h-8 w-12" />
            ) : (
              <p className="text-2xl font-semibold tabular-nums">{done.length}</p>
            )}
          </CardContent>
        </Card>
      </div>

      <Frame>
        <div className="flex items-center justify-between gap-3">
          <h2 className="font-semibold tracking-tight">{t.openRequests}</h2>
          <Button asChild variant="link" size="sm" className="h-auto p-0">
            <Link to="/portal/tasks">{t.seeAll}</Link>
          </Button>
        </div>
        <PortalTaskList
          tasks={open.slice(0, 8)}
          isPending={tasks.isPending}
          emptyTitle={t.noOpenRequest}
        />
      </Frame>

      {/*
        Ce qu'Oyenga a noté comme coordonnées. Le client est le mieux placé
        pour repérer un numéro périmé, encore faut-il qu'il le voie.
      */}
      {(me.data?.client.phones.length ?? 0) + (me.data?.contact.phones.length ?? 0) > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>{t.yourDetails}</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {(me.data?.client.phones.length ?? 0) > 0 && (
              <div className="space-y-1">
                <p className="text-xs font-medium text-muted-foreground">{me.data?.client.name}</p>
                <PhoneList phones={me.data?.client.phones ?? []} />
              </div>
            )}
            {(me.data?.contact.phones.length ?? 0) > 0 && (
              <div className="space-y-1">
                <p className="text-xs font-medium text-muted-foreground">
                  {me.data?.contact.full_name}
                </p>
                <PhoneList phones={me.data?.contact.phones ?? []} />
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>{t.recentActivity}</CardTitle>
        </CardHeader>
        <CardContent>
          {activity.isPending ? (
            <div className="space-y-2">
              {Array.from({ length: 3 }, (_, index) => (
                <Skeleton key={index} className="h-4 w-3/4" />
              ))}
            </div>
          ) : (activity.data?.items.length ?? 0) === 0 ? (
            <p className="text-sm text-muted-foreground">{t.nothingNew}</p>
          ) : (
            <ul className="space-y-3">
              {(activity.data?.items ?? []).map((entry) => (
                <li key={entry.id} className="text-sm">
                  <span className="font-medium">{portalName(entry.actor_name)}</span>{' '}
                  <span className="text-muted-foreground">{describeActivity(entry)}</span>
                  <span className="block text-xs text-muted-foreground">
                    {formatRelative(entry.created_at)}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
